import React, { useEffect } from 'react';
import Chart from 'react-apexcharts';
import { numberWithSpaces } from '../../../helpers/index';
import { getYearColors } from '../../../helpers/reportHelpers';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/macro';
import { registerChartInstance, unregisterChartInstance } from '../../../helpers/exportPdf';

const skGraph = require('apexcharts/dist/locales/sk');
const csGraph = require('apexcharts/dist/locales/cs');
const deGraph = require('apexcharts/dist/locales/de');
const enGraph = require('apexcharts/dist/locales/en');

interface IComparisonRow {
    year: number;
    label: string;
    value: number;
}

interface IProps {
    title: string;
    subtitle?: string;
    rows: IComparisonRow[];
    height?: number;
    /** Unique id passed to the ApexCharts `id` option so we can export it later */
    chartId?: string;
}

/**
 * Bar chart showing year-over-year comparison of a single metric.
 * Each bar = one year. Colors from the brand palette.
 */
const ReportComparisonChart = ({ title, subtitle, rows, height = 400, chartId }: IProps) => {
    const lingui = useLingui();

    // Cleanup on unmount
    useEffect(() => {
        return () => { if (chartId) unregisterChartInstance(chartId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartId]);

    if (!rows || rows.length === 0) return null;

    const categories = rows.map(r => r.label);
    const data = rows.map(r => r.value);
    const colors = getYearColors(rows.length);

    return (
        <div className="mgt-30">
            <h3 className="title_h4">{title}</h3>
            {subtitle && <p className="text_s12 color_grey mgt-5">{subtitle}</p>}
            <Chart
                options={{
                    chart: {
                        id: chartId,
                        toolbar: {
                            show: true,
                            tools: {
                                download: '<span><svg class="sico wh16"><use xlink:href="/assets/layout/icons.svg#s_save"></use></svg>'
                            }
                        },
                        locales: [skGraph, csGraph, enGraph, deGraph],
                        defaultLocale: lingui.i18n.locale,
                        events: chartId ? {
                            mounted: (chartInstance: any) => {
                                registerChartInstance(chartId, chartInstance);
                            },
                            updated: (chartInstance: any) => {
                                registerChartInstance(chartId, chartInstance);
                            },
                        } : undefined,
                    },
                    plotOptions: {
                        bar: {
                            borderRadius: 5,
                            dataLabels: { position: 'top' },
                            distributed: true
                        }
                    },
                    colors,
                    legend: { show: false },
                    dataLabels: {
                        enabled: true,
                        formatter: (val: any) => numberWithSpaces(val),
                        offsetY: -20,
                        style: { fontSize: '12px', colors: ['#304758'] }
                    },
                    stroke: { show: false },
                    xaxis: {
                        categories,
                        labels: { style: { fontSize: '11px' } }
                    },
                    yaxis: {
                        title: { text: t`Počet návštevníkov` },
                        labels: { formatter: (val) => numberWithSpaces(val) }
                    },
                    grid: { strokeDashArray: 10 },
                    fill: { opacity: 1 },
                    tooltip: {
                        y: { formatter: (val: number) => numberWithSpaces(val) }
                    }
                }}
                series={[{ name: t`Návštevnosť`, data }]}
                type="bar"
                height={height}
            />
        </div>
    );
};

export default ReportComparisonChart;